import * as React from "react"

import { cn } from "@/lib/utils"
import { Card, CardContent } from "@/components/ui/card"

/**
 * The one KPI tile.
 *
 * ⭐ WHY THIS IS SHARED RATHER THAN COPIED. LabBOXX had ~140 hand-built tiles
 * across 52 screens, and the Dashboard's local copy took a raw `color` STRING
 * and painted it through inline `style` — `style={{ color }}` for the figure
 * and `` `${color}15` `` for the chip. Inline styles outrank every stylesheet,
 * so those tiles could not follow the tenant's brand, could not follow dark
 * mode, and could not be reached by the contrast gate. Nothing about that is
 * visible in a light-mode review with the default blue brand — which is
 * exactly why it survived.
 *
 * THE COLOUR LAW THIS ENCODES: a role paints a CHIP, a RAIL or a PILL — never
 * a whole panel. The card surface stays `bg-card` in every tone. That is the
 * difference between a dashboard and a bag of sweets, and it is the rule the
 * owner restated every time a tinted panel shipped.
 *
 * `tone` is a vocabulary, not a palette:
 *   brand      the tenant's own colour — counts, totals, anything neutral
 *   success    in range · verified · released · paid
 *   warning    out of range · on hold · expiring · needs attention
 *   destructive  critical / panic · rejected · failed · overdue
 *   info       in progress · received · processing
 *   neutral    a figure with no state at all
 *
 * A tone that can become URGENT belongs to the clinical set; a tone that is
 * merely a fact about what something IS should be `brand` or `neutral`. The
 * test when it is not obvious: could someone need to ACT on this value?
 */
export type Tone = "brand" | "success" | "warning" | "destructive" | "info" | "neutral"

const RAIL: Record<Tone, string> = {
  brand: "border-l-primary",
  success: "border-l-success",
  warning: "border-l-warning",
  destructive: "border-l-destructive",
  info: "border-l-info",
  neutral: "border-l-border",
}

const CHIP: Record<Tone, string> = {
  brand: "bg-primary-100 text-primary-700",
  success: "bg-success-100 text-success-700",
  warning: "bg-warning-100 text-warning-700",
  destructive: "bg-destructive-100 text-destructive-700",
  info: "bg-info-100 text-info-700",
  neutral: "bg-muted text-muted-foreground",
}

/** The SELECTED chip inverts to a solid fill — see `selected` below. */
const CHIP_SOLID: Record<Tone, string> = {
  brand: "bg-primary text-primary-foreground",
  success: "bg-success text-success-foreground",
  warning: "bg-warning text-warning-foreground",
  destructive: "bg-destructive text-destructive-foreground",
  info: "bg-info text-info-foreground",
  neutral: "bg-foreground text-background",
}

const RING: Record<Tone, string> = {
  brand: "ring-primary/40",
  success: "ring-success/40",
  warning: "ring-warning/40",
  destructive: "ring-destructive/40",
  info: "ring-info/40",
  neutral: "ring-border",
}

const FIGURE: Record<Tone, string> = {
  brand: "text-foreground",
  success: "text-success-700",
  warning: "text-warning-700",
  destructive: "text-destructive-700",
  info: "text-info-700",
  neutral: "text-foreground",
}

export interface StatCardProps {
  label: React.ReactNode
  value: React.ReactNode
  /** A lucide icon component, or any element. */
  icon?: React.ElementType
  tone?: Tone
  subtitle?: React.ReactNode
  /**
   * Arbitrary markup under the figure — a progress bar, a sparkline, a link.
   * Distinct from `subtitle`, which is a caption in the label's ink: `foot`
   * spans the full tile width so a meter is not squeezed beside the icon chip.
   * It exists so converting a hand-built tile never has to DROP information to
   * gain the geometry.
   */
  foot?: React.ReactNode
  /** Renders as a button and fires this. */
  onClick?: () => void
  /**
   * A clickable tile's selected state is a RING plus a SOLID icon chip —
   * never a tinted card. Tinting the whole card to show selection is the
   * panel-wash mistake wearing a different hat, and on a row of six tiles it
   * makes the selected one look like an alert.
   */
  selected?: boolean
  className?: string
}

export function StatCard({
  label, value, icon: Icon, tone = "brand", subtitle, foot, onClick, selected, className,
}: StatCardProps) {
  const clickable = typeof onClick === "function"
  return (
    <Card
      /* min-h is platform geometry: a row of tiles whose heights are set by
         whichever happens to have a subtitle reads as a broken grid. */
      className={cn(
        "min-h-24 border-l-4 transition-shadow",
        RAIL[tone],
        clickable && "cursor-pointer hover:shadow-md",
        selected && `ring-2 ${RING[tone]}`,
        className
      )}
      onClick={onClick}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => {
        if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick!() }
      } : undefined}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm text-muted-foreground truncate">{label}</p>
            <p className={cn("text-2xl font-bold mt-0.5", FIGURE[tone])}>{value}</p>
            {subtitle && (
              <p className="text-xs text-muted-foreground mt-0.5 truncate">{subtitle}</p>
            )}
          </div>
          {Icon && (
            <span
              className={cn(
                "inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg",
                selected ? CHIP_SOLID[tone] : CHIP[tone]
              )}
            >
              <Icon className="h-5 w-5" />
            </span>
          )}
        </div>
        {foot && <div className="mt-2">{foot}</div>}
      </CardContent>
    </Card>
  )
}

export default StatCard
